import React, { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Mail, Phone, Clock, MapPin } from "lucide-react";
import { companyInfo } from "../mock";
import { toast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "Almost there",
        description: "Please fill in your name, email and a short message.",
      });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
      });
      toast({
        title: "Message sent — thank you!",
        description: "We'll get back to you in lightning speed.",
      });
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email us directly.";
      toast({
        title: "Couldn't send your message",
        description: detail,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="icg-section">
      <div className="icg-container grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <span className="icg-eyebrow"><span className="icg-dot" /> Contact</span>
          <h2 className="font-display font-semibold tracking-tight mt-5"
              style={{ fontSize: "clamp(40px, 6vw, 88px)", lineHeight: 0.98 }}>
            Let’s make
            <br />
            something<br />
            <span className="font-serif-it text-[color:var(--icg-accent)]">worth talking about</span>.
          </h2>

          <p className="mt-8 text-[color:var(--icg-muted)] leading-relaxed max-w-md">
            Fill out the short form and we promise to get back to you in lightning speed.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-4 group">
              <span className="h-10 w-10 rounded-full bg-[color:var(--icg-ink)] text-[color:var(--icg-cream)] flex items-center justify-center">
                <Mail size={16} />
              </span>
              <div>
                <div className="text-xs text-[color:var(--icg-muted)] uppercase tracking-widest">Email</div>
                <div className="font-medium group-hover:text-[color:var(--icg-accent)] transition-colors">
                  {companyInfo.email}
                </div>
              </div>
            </a>
            <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-4 group">
              <span className="h-10 w-10 rounded-full bg-[color:var(--icg-ink)] text-[color:var(--icg-cream)] flex items-center justify-center">
                <Phone size={16} />
              </span>
              <div>
                <div className="text-xs text-[color:var(--icg-muted)] uppercase tracking-widest">Phone</div>
                <div className="font-medium group-hover:text-[color:var(--icg-accent)] transition-colors">
                  {companyInfo.phone}
                </div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="h-10 w-10 rounded-full bg-[color:var(--icg-ink)] text-[color:var(--icg-cream)] flex items-center justify-center">
                <MapPin size={16} />
              </span>
              <div>
                <div className="text-xs text-[color:var(--icg-muted)] uppercase tracking-widest">Address</div>
                <div className="font-medium">{companyInfo.address}</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="h-10 w-10 rounded-full bg-[color:var(--icg-ink)] text-[color:var(--icg-cream)] flex items-center justify-center">
                <Clock size={16} />
              </span>
              <div>
                <div className="text-xs text-[color:var(--icg-muted)] uppercase tracking-widest">Working hours</div>
                <div className="font-medium">{companyInfo.workingHours}</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="md:col-span-7 md:pl-8">
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="text-xs uppercase tracking-widest text-[color:var(--icg-muted)]">Your name</label>
              <input
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Monica Lockett"
                className="icg-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[color:var(--icg-muted)]">Email</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder="you@company.com"
                className="icg-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-widest text-[color:var(--icg-muted)]">Company (optional)</label>
              <input
                name="company"
                value={form.company}
                onChange={onChange}
                placeholder="Your business"
                className="icg-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-widest text-[color:var(--icg-muted)]">Tell us about your project</label>
              <textarea
                name="message"
                rows={5}
                value={form.message}
                onChange={onChange}
                placeholder="A few words about what you're building…"
                className="icg-input resize-none"
              />
            </div>
          </div>

          <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
            <p className="text-xs text-[color:var(--icg-muted)] max-w-sm">
              By submitting you agree to be contacted about your inquiry. We’ll never share your
              details.
            </p>
            <button type="submit" disabled={loading} className="icg-btn icg-btn--dark">
              {loading ? "Sending…" : "Send message"} <ArrowUpRight size={16} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Contact;
